import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Pencil, Plus, Trash2, UserPlus } from "lucide-react";
import { Button } from "@akuna/ui";
import { cn } from "@/lib/utils";
import { initials } from "@/lib/utils/string-utils";
import { toast } from "@/hooks/use-toast";
import { ConfirmationDialog } from "@/components/ui/confirmation-dialog";
import { useDeleteStayPack, useStayPacks, type StayPack } from "@/hooks/useStayPacks";
import { AddTile, SectionCard, SectionHeader } from "./primitives";
import { colorBarClass, colorDotClass, colorDotStyle } from "./colors";
import PackDialog from "./PackDialog";
import PackMembersDialog from "./PackMembersDialog";

function RecurrenceChips({ pack }: { pack: StayPack }) {
  const { t } = useTranslation("daycareHotel");
  const weekdayLabels = t("packs.weekdayInitials", { returnObjects: true }) as string[];
  const recurrence = pack.recurrence;

  if (!recurrence) {
    return <p className="mb-3.5 text-[12.5px] text-color-light">{t("packs.noRecurrence")}</p>;
  }

  const isMonthly = recurrence.frequency === "monthly";
  if (recurrence.frequency === "daily") {
    return <p className="mb-3.5 text-[12.5px] text-color-light">{t("packs.dailyHint")}</p>;
  }
  const summary = isMonthly
    ? t("packs.summaryMonthly", { count: recurrence.daysOfMonth.length })
    : t("packs.summaryWeekly", { count: recurrence.daysOfWeek.length });

  return (
    <>
      <div className="mb-1.5 flex items-center gap-1.5">
        <span className="text-[11px] font-semibold uppercase tracking-[0.05em] text-color-light">
          {isMonthly ? t("packs.daysOfMonthLabel") : t("packs.daysOfWeekLabel")}
        </span>
        <span className="text-[11.5px] font-semibold text-purple-700">· {summary}</span>
      </div>
      <div className="mb-3.5 flex flex-wrap items-center gap-1.5">
        {isMonthly
          ? recurrence.daysOfMonth.map((day) => (
              <span
                key={day}
                className="flex h-6 min-w-6 items-center justify-center rounded-md bg-orange-50 px-1 text-[11px] font-semibold tabular-nums text-orange-600"
              >
                {day}
              </span>
            ))
          : weekdayLabels.map((label, day) => {
              const active = recurrence.daysOfWeek.includes(day);
              return (
                <span
                  key={day}
                  className={cn(
                    "flex h-6 w-6 items-center justify-center rounded-full text-[11px] font-semibold",
                    active ? "bg-akuna-orange text-primary-foreground" : "bg-muted text-color-light",
                  )}
                >
                  {label}
                </span>
              );
            })}
      </div>
    </>
  );
}

function PackCard({
  pack,
  canEdit,
  canDelete,
  onEdit,
  onManageMembers,
  onDelete,
}: {
  pack: StayPack;
  canEdit: boolean;
  canDelete: boolean;
  onEdit: () => void;
  onManageMembers: () => void;
  onDelete: () => void;
}) {
  const { t } = useTranslation("daycareHotel");
  const preview = pack.members.slice(0, 3);
  const overflow = pack.members.length - preview.length;

  return (
    <SectionCard className="overflow-hidden">
      <div className={cn("h-1.5", colorBarClass(pack.color))} aria-hidden="true" />
      <div className="p-4">
        <div className="mb-1 flex items-center gap-2.5">
          <span className={cn("h-2.5 w-2.5 shrink-0 rounded-full", colorDotClass(pack.color))} style={colorDotStyle(pack.color)} aria-hidden="true" />
          <h3 className="min-w-0 flex-1 truncate font-display text-base font-bold text-foreground">
            {pack.name}
          </h3>
          {canEdit ? (
            <button
              type="button"
              onClick={onEdit}
              aria-label={t("packs.editPack", { name: pack.name })}
              className="flex h-7 w-7 items-center justify-center rounded-full text-color-light transition-colors hover:bg-background-app hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              <Pencil className="h-4 w-4" aria-hidden="true" />
            </button>
          ) : null}
          {canDelete ? (
            <button
              type="button"
              onClick={onDelete}
              aria-label={t("packs.deletePack", { name: pack.name })}
              className="flex h-7 w-7 items-center justify-center rounded-full text-state-danger transition-colors hover:bg-state-danger-soft focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              <Trash2 className="h-4 w-4" aria-hidden="true" />
            </button>
          ) : null}
        </div>

        <RecurrenceChips pack={pack} />

        <div className="mb-2.5 flex items-center">
          {preview.length > 0 ? (
            <div className="flex items-center">
              {preview.map((member, index) => (
                <span
                  key={member.id}
                  title={member.petName}
                  style={{
                    marginLeft: index === 0 ? 0 : -8,
                    zIndex: preview.length - index,
                    ...colorDotStyle(pack.color),
                  }}
                  className={cn(
                    "flex h-8 w-8 items-center justify-center rounded-full border-2 border-card text-[10px] font-bold text-primary-foreground",
                    colorDotClass(pack.color),
                  )}
                >
                  {initials(member.petName)}
                </span>
              ))}
              {overflow > 0 ? (
                <span
                  style={{ marginLeft: -8 }}
                  className="flex h-8 w-8 items-center justify-center rounded-full border-2 border-card bg-muted text-[10px] font-bold text-color-medium"
                >
                  +{overflow}
                </span>
              ) : null}
            </div>
          ) : null}
          <span className={cn("text-[12.5px] font-semibold text-muted-foreground", preview.length > 0 && "ml-2.5")}>
            {t("packs.memberCount", { count: pack.members.length })}
          </span>
        </div>

        <button
          type="button"
          onClick={onManageMembers}
          className="flex w-full items-center justify-center gap-1.5 rounded-[10px] border-[1.5px] border-dashed border-foreground/[0.16] px-2 py-2 text-[12.5px] font-semibold text-color-medium transition-colors hover:bg-background-app focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          <UserPlus className="h-3.5 w-3.5 text-muted-foreground" aria-hidden="true" />
          {t("packs.managePets")}
        </button>
      </div>
    </SectionCard>
  );
}

export default function StayPacksSection({
  canEdit,
  canCreate,
  canDelete,
}: {
  canEdit: boolean;
  canCreate: boolean;
  canDelete: boolean;
}) {
  const { t } = useTranslation("daycareHotel");
  const { data: packs, isLoading } = useStayPacks();
  const deletePack = useDeleteStayPack();

  const [editing, setEditing] = useState<StayPack | null>(null);
  const [isPackDialogOpen, setIsPackDialogOpen] = useState(false);
  const [managing, setManaging] = useState<StayPack | null>(null);
  const [pendingDelete, setPendingDelete] = useState<StayPack | null>(null);

  // The dialogs read from the live query result so a mutation refresh is
  // reflected without reopening them.
  const managingPack = managing ? packs?.find((pack) => pack.id === managing.id) ?? null : null;

  const openCreate = () => {
    setEditing(null);
    setIsPackDialogOpen(true);
  };
  const openEdit = (pack: StayPack) => {
    setEditing(pack);
    setIsPackDialogOpen(true);
  };

  const handleDelete = async () => {
    if (!pendingDelete) return;
    try {
      await deletePack.mutateAsync(pendingDelete.id);
      toast({ title: t("packs.toastDeleted") });
    } catch {
      toast({ variant: "destructive", title: t("packs.toastDeleteFailed") });
    } finally {
      setPendingDelete(null);
    }
  };

  return (
    <section>
      <SectionHeader
        tone="daycare"
        badge={t("badgeDaycare")}
        title={t("packs.title")}
        hint={t("packs.hint")}
        description={t("packs.description")}
        action={
          canCreate ? (
            <Button variant="outline" size="sm" onClick={openCreate}>
              <Plus className="h-4 w-4" aria-hidden="true" />
              {t("packs.newPack")}
            </Button>
          ) : undefined
        }
      />

      {isLoading ? (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {[0, 1, 2].map((index) => (
            <SectionCard key={index} className="h-[220px] animate-pulse bg-muted/40" />
          ))}
        </div>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {(packs ?? []).map((pack) => (
            <PackCard
              key={pack.id}
              pack={pack}
              canEdit={canEdit}
              canDelete={canDelete}
              onEdit={() => openEdit(pack)}
              onManageMembers={() => setManaging(pack)}
              onDelete={() => setPendingDelete(pack)}
            />
          ))}
          {canCreate ? (
            <AddTile
              onClick={openCreate}
              tone="daycare"
              minHeight="200px"
              icon={<Plus className="h-5 w-5" aria-hidden="true" />}
              title={t("packs.newPack")}
            >
            </AddTile>
          ) : null}
        </div>
      )}

      <PackDialog open={isPackDialogOpen} onOpenChange={setIsPackDialogOpen} pack={editing} />
      <PackMembersDialog
        open={!!managing}
        onOpenChange={(open) => !open && setManaging(null)}
        pack={managingPack}
        canEdit={canEdit}
      />
      <ConfirmationDialog
        open={!!pendingDelete}
        onOpenChange={(open) => !open && setPendingDelete(null)}
        title={t("packs.deleteTitle")}
        description={t("packs.deleteDescription", { name: pendingDelete?.name ?? "" })}
        confirmText={t("packs.deleteConfirm")}
        cancelText={t("common.cancel")}
        variant="destructive"
        onConfirm={handleDelete}
      />
    </section>
  );
}
